import React from "react"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useLanguage } from "@/hooks/useLanguage"
import { formatUnitsBreakdown, formatTotalWeight, formatCurrency } from "@/lib/productDisplay"
import { ivaAmount } from "@/lib/tax"
import { cn } from "@/lib/utils"

interface CheckoutSummaryProps {
  items: any[]
  totalPrice: number
  isSubmitting: boolean
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>
  text: any
}

export const CheckoutSummary: React.FC<CheckoutSummaryProps> = ({
  items,
  totalPrice,
  isSubmitting,
  onSubmit,
  text,
}) => {
  const { t } = useLanguage()
  const shouldConstrainItems = items.length > 4
  const iva = items.reduce((sum, item) => {
    return sum + (item.isTaxable ? ivaAmount((item.price || 0) * item.quantity) : 0)
  }, 0)
  const total = totalPrice + iva

  return (
    <div className="overflow-hidden rounded-lg bg-foreground/10 p-px shadow-2xl">
      <div className="space-y-10 bg-card/95 p-10 backdrop-blur-md">
        <div className="flex items-center justify-between border-b-[1px] border-foreground/5 pb-4">
          <h2 className="font-display text-4xl uppercase tracking-tighter">{text.summary}</h2>
          <span className="text-xs font-black tracking-[0.2em] text-accent">
            {items.length} {text.items}
          </span>
        </div>

        <ScrollArea
          data-testid="checkout-summary-items"
          className={cn(
            "pr-1",
            shouldConstrainItems ? "h-[clamp(16rem,40vh,28rem)]" : "max-h-[40vh]"
          )}
        >
          <div className="space-y-6 pr-4">
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-display text-xl tracking-wider text-foreground">
                    {item.name}
                  </p>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    {formatUnitsBreakdown(item.quantity, item.weight, item.weightUnit, t.units)}
                  </p>
                  {item.weight && item.weight > 0 && (
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60">
                      {formatTotalWeight(item.quantity, item.weight, item.weightUnit, t.units.total)}
                    </p>
                  )}
                </div>
                <span className="font-black tabular-nums text-foreground">
                  {formatCurrency((item.price || 0) * item.quantity)}
                </span>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="space-y-3 border-t-[1px] border-foreground/5 pt-8">
          <div className="flex justify-between text-xs font-bold uppercase tracking-label text-muted-foreground">
            <span>{text.subtotal}</span>
            <span className="tabular-nums">{formatCurrency(totalPrice)}</span>
          </div>
          <div className="flex justify-between text-xs font-bold uppercase tracking-label text-muted-foreground">
            <span>{text.iva}</span>
            <span className="tabular-nums">{formatCurrency(iva)}</span>
          </div>
          <Separator className="my-6 bg-foreground/5" />
          <div className="flex items-end justify-between">
            <span className="font-display text-4xl uppercase tracking-tighter">
              {text.total}
            </span>
            <span className="font-display text-4xl tabular-nums tracking-widest text-accent">
              {formatCurrency(total)}
            </span>
          </div>
        </div>

        <Button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="h-auto w-full rounded-lg bg-action py-8 font-display text-3xl tracking-label text-action-foreground shadow-2xl transition-all hover:scale-[1.01] hover:brightness-110 active:scale-95 disabled:opacity-50"
        >
          {isSubmitting ? text.processing : text.placeOrder}
        </Button>
      </div>
    </div>
  )
}
